/**
 * Route helpers.
 *
 * The template this started from resolved paths with `path-to-regexp`, to
 * support routes carrying parameters (`/listing/:id/:slug`). No route here
 * has a parameter — they are four literal paths — so compiling them was
 * machinery around a lookup, and `pathByRouteName` even rewrote an empty
 * `slug` param to 'no-slug' for a route shape that does not exist.
 *
 * `matchPathname` and `createResourceLocatorString` are gone too: nothing
 * called either, and `matchPathname` carried a latent bug — it tested
 * `exact` on its own result objects rather than on the route, so the check
 * was always `undefined` and it silently returned the first match.
 */

const findRouteByName = (nameToFind, routes) => routes.find(route => route.name === nameToFind);

/**
 * Looks up a route by name.
 *
 * @throws if no route has that name — a typo'd route name should fail
 *   loudly at the call site, not render a link to nowhere.
 */
const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Route "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * The path for a named route, e.g. pathByRouteName('LandingPage') => '/'.
 */
export const pathByRouteName = (nameToFind, routes) =>
  findRouteByRouteName(nameToFind, routes).path;

/**
 * The canonical path for a router location.
 *
 * `pathOnly` drops the query and hash, which is what a canonical URL wants
 * when the referrer policy means they should not be published.
 */
export const canonicalRoutePath = (location, pathOnly = false) => {
  const { pathname, search, hash } = location;
  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};
